import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  GUEST_CART_KEY as CART_KEY,
  GUEST_SESSION_KEY as SESSION_KEY,
  clearGuestState,
  openQrRequest,
  setGuestToken,
  setTenantId,
} from '@/lib/api';
import { disconnectSocket } from '@/lib/realtime';
import {
  DEFAULT_TRANSPARENCY,
  isPinRequired,
  errorMessage,
  normalizeSession,
  type GuestSession,
} from '@/lib/guest';

export type { GuestSession } from '@/lib/guest';

export interface CartLine {
  itemId: string;
  name: string;
  unitPrice: number;
  quantity: number;
  notes?: string;
  modifierIds?: string[];
}

interface GuestState {
  session: GuestSession | null;
  cart: CartLine[];
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  /** The venue asked for a table PIN; the scan screen switches to it. */
  pinRequired: boolean;
  /**
   * How many cart lines were thrown away because the guest moved to a different
   * table. Zero unless that just happened; the scan screen says so.
   */
  discardedCart: number;
}

/** A refresh must not throw the guest back to the scan screen mid-meal. */
function restore<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

const restoredSession = restore<GuestSession | null>(SESSION_KEY, null);

const initialState: GuestState = {
  // A session persisted by an older build may predate the extra fields.
  session: restoredSession
    ? {
        ...restoredSession,
        transparency: restoredSession.transparency ?? DEFAULT_TRANSPARENCY,
        // A session persisted before the PIN was surfaced simply has none to
        // show; the table screen says so rather than showing `undefined`.
        sessionPin: restoredSession.sessionPin ?? null,
        // A session persisted before `qrToken` existed cannot prove which table
        // it belongs to, so any scanned token is treated as a different one.
        qrToken: restoredSession.qrToken ?? null,
      }
    : null,
  // A line with no `itemId` cannot be ordered — the API refuses it with
  // `items.0.itemId should not be empty` — and one persisted by an older build
  // would otherwise sit in the cart forever, failing every checkout with no way
  // for the guest to tell WHICH line is bad. Drop them on load.
  cart: restore<CartLine[]>(CART_KEY, []).filter((line) => typeof line?.itemId === 'string' && line.itemId),
  status: restoredSession ? 'ready' : 'idle',
  error: null,
  pinRequired: false,
  discardedCart: 0,
};

/**
 * The QR token identifies the table/resource; the API answers with a
 * scoped guest session. No app download, no account required.
 *
 * `pin` is sent when the venue has put one on the table. The client
 * cannot know that in advance, so it tries without and asks only if refused.
 */
export const openQrSession = createAsyncThunk<
  GuestSession,
  { qrToken: string; pin?: string },
  { rejectValue: { message: string; pinRequired: boolean } }
>('guest/openSession', async (payload, { rejectWithValue }) => {
  try {
    // Deliberately identity-free: `openQrRequest` strips the previous table's
    // tenant header and bearer token, so the QR alone decides which venue and
    // which table this is.
    const { data } = await openQrRequest({
      qrToken: payload.qrToken,
      ...(payload.pin ? { pin: payload.pin } : {}),
    });
    // The originating token is kept on the session — it is what tells a later
    // scan whether this is the same table or a different one.
    const session = normalizeSession(data, payload.qrToken);
    // Both credentials the rest of the app needs: the bearer token and the
    // tenant every later request is resolved against.
    setGuestToken(session.token);
    setTenantId(session.tenantId);
    // The socket must be rebuilt on the new identity, never reused anonymously.
    disconnectSocket();
    return session;
  } catch (error) {
    return rejectWithValue({
      message: errorMessage(error, 'Could not open this table'),
      pinRequired: isPinRequired(error) && !payload.pin,
    });
  }
});

const guestSlice = createSlice({
  name: 'guest',
  initialState,
  reducers: {
    addToCart(state, action: PayloadAction<CartLine>) {
      // Refuse at the door rather than at checkout. Guest order items did not
      // carry `itemId` until recently, so "order this again" built lines the API
      // could never accept.
      if (!action.payload?.itemId) return;
      const existing = state.cart.find(
        (l) => l.itemId === action.payload.itemId && l.notes === action.payload.notes,
      );
      if (existing) existing.quantity += action.payload.quantity;
      else state.cart.push(action.payload);
      persistCart(state.cart);
    },
    /** One tap puts a previous order's lines back in the cart. */
    addManyToCart(state, action: PayloadAction<CartLine[]>) {
      for (const line of action.payload) {
        if (!line?.itemId) continue;
        const existing = state.cart.find((l) => l.itemId === line.itemId && l.notes === line.notes);
        if (existing) existing.quantity += line.quantity;
        else state.cart.push(line);
      }
      persistCart(state.cart);
    },
    setQuantity(state, action: PayloadAction<{ itemId: string; quantity: number }>) {
      const line = state.cart.find((l) => l.itemId === action.payload.itemId);
      if (!line) return;
      if (action.payload.quantity <= 0) {
        state.cart = state.cart.filter((l) => l.itemId !== action.payload.itemId);
      } else {
        line.quantity = action.payload.quantity;
      }
      persistCart(state.cart);
    },
    clearCart(state) {
      state.cart = [];
      persistCart(state.cart);
    },
    clearPinPrompt(state) {
      state.pinRequired = false;
      state.error = null;
    },
    /** The scan screen has shown the "your basket was cleared" line. */
    acknowledgeDiscardedCart(state) {
      state.discardedCart = 0;
    },
    leaveSession(state) {
      state.session = null;
      state.cart = [];
      state.status = 'idle';
      state.error = null;
      state.pinRequired = false;
      state.discardedCart = 0;
      clearGuestState();
      disconnectSocket();
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * Opening a QR **replaces** the guest; it never merges into the old one.
       *
       * Session id, guest token, joining PIN and cart routing all belong to one
       * visit at one table. Keeping any of them while opening another table is
       * what put a guest's order on a stranger's check, so the previous state is
       * torn down here — before the network call, so a slow request cannot
       * leave the app half on each table.
       *
       * **The mid-build cart goes with it.** A cart line is an item id priced
       * from one branch's menu and routed to one visit's check; at a different
       * table (often a different branch, sometimes a different tenant) those ids
       * may not exist and the prices may differ, so silently
       * re-pointing it would be the same class of bug this fixes, one step
       * later. It is discarded — and the count is kept so the scan screen can
       * tell the guest rather than letting a basket vanish.
       *
       * Re-opening the *same* token (a page reload, a re-scan of the table the
       * guest is already sitting at, a retry with the PIN) keeps the cart.
       */
      .addCase(openQrSession.pending, (state, action) => {
        const sameTable = state.session?.qrToken === action.meta.arg.qrToken;
        if (!sameTable) {
          // Only a real replacement writes the count — a PIN retry (session
          // already gone, cart already empty) must not wipe the notice.
          if (state.session) state.discardedCart = state.cart.length;
          state.session = null;
          state.cart = [];
          clearGuestState();
          disconnectSocket();
        }
        state.status = 'loading';
        state.error = null;
      })
      .addCase(openQrSession.fulfilled, (state, action) => {
        state.status = 'ready';
        state.session = action.payload;
        state.pinRequired = false;
        localStorage.setItem(SESSION_KEY, JSON.stringify(action.payload));
      })
      .addCase(openQrSession.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload?.message ?? 'Could not open this table';
        state.pinRequired = action.payload?.pinRequired ?? false;
      });
  },
});

function persistCart(cart: CartLine[]): void {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch {
    // A full or disabled localStorage must never cost the guest their cart
    // in memory; persistence is a convenience, not the source of truth.
  }
}

export const {
  acknowledgeDiscardedCart,
  addToCart,
  addManyToCart,
  setQuantity,
  clearCart,
  clearPinPrompt,
  leaveSession,
} = guestSlice.actions;
export default guestSlice.reducer;

export const cartTotal = (cart: CartLine[]): number =>
  cart.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
